import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: ${theme.maxWidth};
  margin: 0 auto;
  padding: ${theme.spacing.medium};
  background-color: ${theme.colors.background};
`;

const Title = styled.h1`
  color: ${theme.colors.titleText};
  text-align: center;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.small};
  width: 100%;
  max-width: 500px;
  margin: 10px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.small};
`;

const Label = styled.label`
  color: ${theme.colors.text};
  font-weight: 500;
  font-size: 0.95rem;
`;

const Input = styled.input`
  padding: ${theme.spacing.small};
  border: 2px solid ${theme.colors.text};
  border-radius: 4px;
  background-color: ${theme.colors.background};
  color: ${theme.colors.text};
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.titleText};
  }

  &::placeholder {
    color: ${theme.colors.text}80;
  }
`;

const SubmitButton = styled.button`
  padding: ${theme.spacing.small} ${theme.spacing.medium};
  background-color: ${theme.colors.titleText};
  color: ${theme.colors.background};
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.3s ease;
  margin-top: ${theme.spacing.medium};

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`;

const Text = styled.p`
  color: ${theme.colors.text};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.medium};
`;

const Info = styled.p`
  color: ${theme.colors.ufiGreenLight};
  border: 1px solid ${theme.colors.ufiGreenLight};
  border-radius: 4px;
  padding: ${theme.spacing.small};
  text-align: center;
  max-width: 500px;
  line-height: 1.6;
  font-size: small;
  margin-bottom: ${theme.spacing.medium};
`;

const ErrorText = styled.span`
  color: #e74c3c;
  font-size: 0.85rem;
  margin-top: 2px;
`;

interface ProfileFormData {
  clientHash: string;
  profileName: string;
  hostUrl: string;
  username: string;
  password: string;
}

interface FormErrors {
  profileName?: string;
  hostUrl?: string;
  username?: string;
  password?: string;
}

const HOST_URL_PATTERN = /^https?:\/\//i;

const RECAPTCHA_ACTION = 'submit_profile';

function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (window.grecaptcha?.execute) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
    document.head.appendChild(script);
  });
}

async function getRecaptchaToken(siteKey: string): Promise<string> {
  return new Promise((resolve, reject) => {
    window.grecaptcha!.ready(async () => {
      try {
        const token = await window.grecaptcha!.execute(siteKey, { action: RECAPTCHA_ACTION });
        resolve(token);
      } catch (err) {
        reject(err);
      }
    });
  });
}

const Profile: React.FC = () => {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '';
  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    clientHash: '',
    profileName: '',
    hostUrl: '',
    username: '',
    password: '',
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clientHash = params.get('clientHash') || '';
    setFormData(prev => ({
      ...prev,
      clientHash
    }));
  }, []);

  useEffect(() => {
    if (!siteKey) return;
    loadRecaptchaScript(siteKey).then(() => setRecaptchaReady(true)).catch(() => {});
  }, [siteKey]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user types
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const trimmed = {
      profileName: formData.profileName.trim(),
      hostUrl: formData.hostUrl.trim(),
      username: formData.username.trim(),
      password: formData.password,
    };

    if (!trimmed.profileName) {
      newErrors.profileName = 'Profile name is required';
    }
    if (!trimmed.hostUrl) {
      newErrors.hostUrl = 'Host URL is required';
    } else if (!HOST_URL_PATTERN.test(trimmed.hostUrl)) {
      newErrors.hostUrl = 'Host URL must start with http:// or https://';
    }
    if (!trimmed.username) {
      newErrors.username = 'Username is required';
    }
    if (!trimmed.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (siteKey && !recaptchaReady) {
      setErrorMessage(true);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(false);

    try {
      let recaptchaToken: string | undefined;
      if (siteKey) {
        recaptchaToken = await getRecaptchaToken(siteKey);
      }

      const response = await fetch('/api/profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          ...(recaptchaToken && { recaptchaToken }),
        }),
      });

      if (response.ok) {
        setSuccessMessage(true);
        setErrorMessage(false);
      } else {
        setSuccessMessage(false);
        setErrorMessage(true);
      }
    } catch {
      setSuccessMessage(false);
      setErrorMessage(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Title>Create TV Profile</Title>
      <Info>This is a one-time pairing request and the data will be deleted immediately after pairing attempt.</Info> 
      <FormContainer onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="profileName">Profile Name</Label>
          <Input
            id="profileName"
            name="profileName"
            type="text"
            placeholder="Give your profile a name"
            value={formData.profileName}
            onChange={handleInputChange}
            aria-invalid={!!errors.profileName}
            aria-describedby={errors.profileName ? 'profileName-error' : undefined}
          />
          {errors.profileName && <ErrorText id="profileName-error" role="alert">{errors.profileName}</ErrorText>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="hostUrl">Host URL (XTream only)</Label>
          <Input
            id="hostUrl"
            name="hostUrl"
            type="url"
            placeholder="Enter host URL (e.g., http://example.com:8080)"
            value={formData.hostUrl}
            onChange={handleInputChange}
            aria-invalid={!!errors.hostUrl}
            aria-describedby={errors.hostUrl ? 'hostUrl-error' : undefined}
          />
          {errors.hostUrl && <ErrorText id="hostUrl-error" role="alert">{errors.hostUrl}</ErrorText>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="username">Username</Label>
          <Input
            id="username"
            name="username"
            type="text"
            placeholder="Enter username"
            value={formData.username}
            onChange={handleInputChange}
            aria-invalid={!!errors.username}
            aria-describedby={errors.username ? 'username-error' : undefined}
          />
          {errors.username && <ErrorText id="username-error" role="alert">{errors.username}</ErrorText>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            name="password"
            type="password"
            placeholder="Enter password"
            value={formData.password}
            onChange={handleInputChange}
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'password-error' : undefined}
          />
          {errors.password && <ErrorText id="password-error" role="alert">{errors.password}</ErrorText>}
        </FormGroup>

        <SubmitButton type="submit" disabled={successMessage || isSubmitting}>
          {isSubmitting ? 'Verifying...' : 'Pair with my TV'}
        </SubmitButton>
      </FormContainer>
      <Text id="successMessage" style={{ display: successMessage ? 'block' : 'none', color: theme.colors.ufiGreen }}>
        Success! Wait for the TV app to pair.
      </Text>
      <Text id="errorMessage" style={{ display: errorMessage ? 'block' : 'none', color: 'red' }}>
        Failed to pair profile request.
      </Text>
    </Container>
  );
};

export default Profile;